import { useEffect, useState } from "react";
import { Copy, Trash2, UserPlus, Loader2, Crown, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

export function TeamCard() {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  if (!team?.group) return null;
  const { group, members, invites = [], seats } = team;
  const used = members.length + invites.length;

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team/invites", { email });
      if (data.email_sent) toast.success(`Invite emailed to ${data.email}`);
      else {
        await navigator.clipboard?.writeText(inviteLink(data.token)).catch(() => {});
        toast.warning("Invite created, but the email couldn't be sent. Link copied — send it yourself.");
      }
      setEmail("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <section className="rounded-2xl border bg-white p-6 sm:p-7 space-y-6" data-testid="team-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{group.name}</h2>
          <p className="text-sm text-slate-500">{group.is_owner ? "Members each keep their own private clients and notes." : `Your plan is covered by ${group.owner_name}.`}</p>
        </div>
        <span className="font-mono text-sm px-3 py-1 rounded-full bg-secondary text-primary" data-testid="team-seats">{used} / {seats} seats</span>
      </div>

      {group.is_owner && group.active && (
        <form onSubmit={invite} className="flex gap-2">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@clinic.com" className="h-10" data-testid="team-invite-email-input" />
          <Button type="submit" className="rounded-full" disabled={busy || used >= seats} data-testid="team-invite-button">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}Invite
          </Button>
        </form>
      )}

      <div className="divide-y rounded-xl border">
        {members.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`team-member-${m.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium flex items-center gap-1.5">{m.name}{m.is_owner && <Crown className="h-3.5 w-3.5 text-amber-500" />}</div>
              <div className="text-xs text-slate-500 truncate">{m.email} · {m.role}</div>
            </div>
            {group.is_owner && !m.is_owner && (
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-600" data-testid={`team-remove-${m.id}`}
                onClick={() => window.confirm(`Remove ${m.name} from the team?`) && act(() => api.delete(`/team/members/${m.id}`), "Member removed")}>
                Remove
              </Button>
            )}
          </div>
        ))}
        {invites.map((i) => (
          <div key={i.token} className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50/40" data-testid={`team-invite-${i.email}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{i.email}</div>
              <div className="text-xs text-amber-700">{i.last_sent_at ? "Invite pending · emailed" : "Invite pending · not emailed — copy the link or resend"}</div>
            </div>
            <div className="flex">
              <Button variant="ghost" size="sm" data-testid={`team-resend-invite-${i.email}`} onClick={() => act(() => api.post(`/team/invites/${i.token}/resend`), `Invite re-sent to ${i.email}`)}>
                <Send className="h-4 w-4 mr-1" />Resend
              </Button>
              <Button variant="ghost" size="sm" data-testid={`team-copy-invite-${i.email}`} onClick={() => { navigator.clipboard.writeText(inviteLink(i.token)); toast.success("Invite link copied"); }}>
                <Copy className="h-4 w-4 mr-1" />Copy link
              </Button>
              <Button variant="ghost" size="icon" className="hover:text-red-600" data-testid={`team-revoke-${i.email}`} onClick={() => act(() => api.delete(`/team/invites/${i.token}`), "Invite revoked")}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {!group.is_owner && (
        <Button variant="outline" className="rounded-full" data-testid="team-leave-button"
          onClick={() => window.confirm("Leave this team? You'll need your own plan to keep using the app.") && act(async () => { await api.post("/team/leave"); window.location.reload(); }, "You left the team")}>
          Leave team
        </Button>
      )}
    </section>
  );
}
